import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import type { Placement } from '@popperjs/core';
import { createPortal } from 'react-dom';

type TooltipProps = {
  children: React.ReactNode;
  tooltip: React.ReactNode;
  placement: Placement;
  className?: string;
  delayMs?: number;
};

export const DarkTooltip: React.FC<TooltipProps> = ({
  children,
  tooltip,
  placement,
  className,
  delayMs = 0,
}: TooltipProps) => {
  const referenceRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const showTimerRef = useRef<number | undefined>(undefined);
  const [isMouseOn, setIsMouseOn] = useState<boolean>(false);

  useEffect(() => () => window.clearTimeout(showTimerRef.current), []);
  const { styles, attributes, update } = usePopper(referenceRef.current, tooltipRef.current, {
    placement,
    strategy: 'fixed',
    modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
  });

  useLayoutEffect(() => {
    if (isMouseOn) {
      update?.();
    }
  }, [update, isMouseOn]);

  return (
    <>
      <div
        className={className}
        ref={referenceRef}
        onMouseEnter={() => {
          if (delayMs > 0) {
            showTimerRef.current = window.setTimeout(() => setIsMouseOn(true), delayMs);
          } else {
            setIsMouseOn(true);
          }
        }}
        onMouseLeave={() => {
          window.clearTimeout(showTimerRef.current);
          setIsMouseOn(false);
        }}
      >
        {children}
      </div>
      {createPortal(
        <div ref={tooltipRef} style={styles.popper} {...attributes.popper}>
          {isMouseOn && tooltip && (
            <div className="flex items-center justify-center rounded-lg border border-gray-400 bg-gray-700 px-3 py-2 text-sm text-white shadow-lg">
              {tooltip}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};
